import {
  ClientHoldingPattern,
  Mandate,
  PaginatedResponse,
  PlanStatus,
  TransactionOrder,
  InvestorBankDetail,
  SubmittedOrder,
} from "../types";
import { api } from "./axios";

export type NotificationType =
  | "BANK_ACCOUNT_VERIFIED"
  | "BANK_ACCOUNT_VERIFICATION_FAILED"
  | "CLIENT_CREATED"
  | "CLIENT_CONSENT_RECORDED"
  | "CLIENT_ACTIVATED"
  | "CLIENT_DETAILS_UPDATED"
  | "KYC_VALIDATED"
  | "KYC_FAILED"
  | "MANDATE_CREATED"
  | "MANDATE_ACTIVATED"
  | "MANDATE_APPROVED"
  | "MANDATE_REJECTED"
  | "MFCENTRAL_FETCH"
  | "ORDER_SUBMITTED"
  | "ORDER_SUBMISSION_FAILED"
  | "ORDER_PAYMENT_COMPLETED"
  | "ORDER_PROCESSED"
  | "ORDER_PROCESSING_FAILED"
  | "STP_ACTIVATED"
  | "STP_CANCELLED"
  | "STP_COMPLETED"
  | "SWP_ACTIVATED"
  | "SWP_CANCELLED"
  | "SWP_COMPLETED"
  | "XSIP_ACTIVATED"
  | "XSIP_CANCELLED"
  | "XSIP_COMPLETED"
  | "XSIP_PAUSED"
  | "CHANGE_OF_BROKER";

type BaseNotificationDetails = {
  client: {
    id: number;
    name: string;
    // Sent by the family recent-activity API
    holding_pattern?: ClientHoldingPattern;
  };
};

type BaseNotification<
  T extends NotificationType,
  U extends BaseNotificationDetails = BaseNotificationDetails,
> = {
  id: number;
  distributor: number;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  event_type: T;
  details: U;
};

type MandateNotificationDetails = BaseNotificationDetails & {
  mandate: Mandate;
};

type ClientNotification = BaseNotification<
  | "CLIENT_CREATED"
  | "CLIENT_CONSENT_RECORDED"
  | "CLIENT_ACTIVATED"
  | "CLIENT_DETAILS_UPDATED"
  | "KYC_VALIDATED"
  | "KYC_FAILED"
  | "MFCENTRAL_FETCH"
>;

type MandateNotification = BaseNotification<
  | "MANDATE_CREATED"
  | "MANDATE_ACTIVATED"
  | "MANDATE_APPROVED"
  | "MANDATE_REJECTED",
  MandateNotificationDetails
>;

type SubmittedOrderNotificationDetails = BaseNotificationDetails & {
  orders: SubmittedOrder[];
};

type TransactionOrderNotificationDetails = BaseNotificationDetails & {
  orders: TransactionOrder[];
};

export type PlanStatusUpdate = {
  id: number;
  new_status: PlanStatus;
  old_status: PlanStatus;
};

export function isTransactionOrderNotificationDetails(
  detail: BaseNotificationDetails
): detail is TransactionOrderNotificationDetails {
  return "orders" in detail;
}

type PlanStatusNotificationDetails = BaseNotificationDetails & {
  updates: PlanStatusUpdate[];
};

export function isPlanStatusNotificationDetails(
  detail: BaseNotificationDetails
): detail is PlanStatusNotificationDetails {
  return "updates" in detail;
}

export type BankNotificationDetails = BaseNotificationDetails & {
  bank_account: InvestorBankDetail;
};

export function isBankNotificationDetails(
  detail: BaseNotificationDetails
): detail is BankNotificationDetails {
  return "bank_account" in detail;
}

export function isMandateNotificationDetails(
  detail: BaseNotificationDetails
): detail is MandateNotificationDetails {
  return "mandate" in detail;
}

export function isSubmittedOrderNotificationDetails(
  detail: BaseNotificationDetails
): detail is SubmittedOrderNotificationDetails {
  return "orders" in detail;
}

type BankNotification = BaseNotification<
  "BANK_ACCOUNT_VERIFIED" | "BANK_ACCOUNT_VERIFICATION_FAILED",
  BankNotificationDetails
>;

type OrderNotification = BaseNotification<
  | "ORDER_SUBMITTED"
  | "ORDER_SUBMISSION_FAILED"
  | "ORDER_PAYMENT_COMPLETED"
  | "ORDER_PROCESSED"
  | "ORDER_PROCESSING_FAILED",
  SubmittedOrderNotificationDetails
>;

export type Notification =
  | ClientNotification
  | MandateNotification
  | OrderNotification
  | BankNotification;

export function getNotifications({
  page,
  pageSize = 20,
  importantOnly,
  clientId,
  minDate,
}: {
  page: number;
  pageSize: number;
  importantOnly?: boolean;
  clientId?: number;
  minDate?: string;
}): Promise<PaginatedResponse<Notification>> {
  return api.get("distributor-notifications/", {
    params: {
      page,
      page_size: pageSize,
      important: importantOnly,
      client_id: clientId,
      min_date: minDate,
    },
  });
}

export function getUnreadNotificationCount(importantOnly?: boolean): Promise<{
  unread_count: number;
}> {
  return api.get("distributor-notifications/unread_count/", {
    params: {
      important: importantOnly,
    },
  });
}

export function markAsRead(params?: {
  id?: number;
  importantOnly?: boolean;
}): Promise<Notification> {
  return api.patch(
    `distributor-notifications/mark_read/`,
    params?.id ? { id: params.id } : { all: true },
    { params: { important: params?.importantOnly } }
  );
}

export function markAsUnread({ id }: { id: number }): Promise<Notification> {
  return api.patch(`distributor-notifications/${id}/mark_unread/`);
}
